// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color } from "@rugby/XDL/Utilities";
import CustomMediaQuery from "@rugby/XDL/Utilities/MediaQuery";
import { css } from "styled-components";

const MediaQuery = CustomMediaQuery();

export interface SlideshowStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    innerContentWrapper?: InjectableCss;
    innerContent?: InjectableCss;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    navigationContainer?: InjectableCss;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
}

const arrowIconContainer = (position: "left" | "right") => {
    return css`
        z-index: 1;
        transform: translateY(-50%;);

        position: absolute;
        top: 50%;
        ${position}: 30px;
        ${MediaQuery.lt("xl")} {
            ${position}: 5px;
        }
        ${MediaQuery.lt("600")} {
            ${position}: -6px;
        }
        ${MediaQuery.gte("xl")} {
            ${position}: calc((100% - 1440px) / 2);
        }
    `;
};

// <XNG-Changes>
const buttonIconWrapper = css`
    width: 30px;
    height: 30px;
    min-height: 30px;

    padding: 0;
    border-radius: 100%;
    background-color: transparent;
    border: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background: ${getColor("common.white")};

    &:hover {
        background: ${getColor("primary")};
        svg {
            color: ${getColor("common.white")};
        }
    }
    &:focus {
        outline: none;
    }
`;
// </XNG-Changes>

export const slideshowStyles: SlideshowStyles = {
    slideshowWrapper: {
        css: css`
            padding: 0;
            position: relative;
        `,
    },
    iconProps: {
        size: 26,
        color: "primary",
        css: css`
            transform: ${(props: any) => {
                const offsetX = 1.5;
                const refactor = props.position === "left" ? -1 : 1;
                return `translateX(${offsetX * refactor}px);`;
            }};
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideContentWrapper: {
        css: css`
            flex: 0 0 100%;
            position: relative;
            color: ${getColor("text.main")};
            padding: 0;
        `,
    },
    innerContentWrapper: {
        css: css`
            text-align: left;
            display: flex;
            align-items: stretch;
            height: 100%;

            &.left &.topLeft,
            &.centerLeft,
            &.bottomLeft {
                justify-content: flex-start;

                ${MediaQuery.lte("md")} {
                    margin-left: 0;
                    margin-right: 0;
                }
            }

            &.right,
            &.topRight,
            &.centerRight,
            &.bottomRight {
                margin-right: 0;
                text-align: right;
                justify-content: flex-end;

                ${MediaQuery.lte("md")} {
                    margin-left: 0;
                    margin-right: 0;
                }
            }

            &.topCenter,
            &.center,
            &.bottomCenter {
                justify-content: center;

                ${MediaQuery.lte("md")} {
                    padding: 45px 10px;
                    margin-left: 0;
                    margin-right: 0;
                    background: ${color("common.white", 0.9)};
                }

                ${MediaQuery.lte("lg")} {
                    padding: 45px 10px;
                }
            }
        `,
    },
    innerContent: {
        css: css`
            width: 40%;
            padding: 45px 5%;
            background: rgba(255, 255, 255, 1);

            ${MediaQuery.lte("lg")} {
                width: 60%;
            }

            ${MediaQuery.lte("md")} {
                width: 90%;
                padding: 45px 7%;
                background: ${color("common.white", 0.9)};
            }

            ${MediaQuery.gte("xl")} {
                width: 45%;
            }

            &.left &.topLeft,
            &.centerLeft,
            &.bottomLeft {
                text-align: left;

                ${MediaQuery.gte("xl")} {
                    padding-left: calc((100% - 1350px) / 2);
                }
            }

            &.right &.topRight,
            &.centerRight,
            &.bottomRight {
                text-align: right;

                ${MediaQuery.gte("xl")} {
                    padding-right: calc((100% - 1350px) / 2);
                }
            }

            &.topCenter,
            &.center,
            &.bottomCenter {
                text-align: center;
            }
        `,
    },
    headingText: {
        size: 42,
        css: css`
            font-size: 42px;

            h1,
            h2,
            h3,
            h4 {
                margin-bottom: 6px;
                font-size: 42px;
                font-weight: normal;
            }

            p {
                font-size: 26px;
            }
        `,
    },
    subheadingText: {
        size: 26,
        css: css`
            font-size: 26px;

            h1,
            h2,
            h3,
            h4 {
                font-size: 42px;
                font-weight: normal;
            }
            p {
                font-size: 26px;
                margin-bottom: 24px;
            }
        `,
    },
    slideButton: {
        css: css`
            width: fit-content;
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: 15px;
        `,
    },
    dotButton: {
        css: css`
            height: 10px;
            width: 10px;
            padding: 0;
            margin: 0 4px;
            border: 0;
            border-radius: 100%;
            background: ${getColor("secondary.main")};
            &:hover,
            &.active {
                background: ${getColor("common.ui1")};
            }
            &:focus {
                outline: none;
            }
        `,
    },
    navigationContainer: {
        css: css`
            position: absolute;
            right: 40px;
            bottom: 20px;
            z-index: 1;
            display: flex;
        `,
    },
    prevArrowWrapper: {
        css: arrowIconContainer("left"),
    },
    prevArrowButton: {
        css: buttonIconWrapper,
    },
    nextArrowWrapper: {
        css: arrowIconContainer("right"),
    },
    nextArrowButton: {
        css: buttonIconWrapper,
    },
};
